import {
  FinanceInvestigationReceiptState,
  FinanceInvestigationReconciliationState,
  InvestigationCheckId,
  InvestigationCheckStatus,
  LATE_ENTRY_THRESHOLD_DAYS,
  entryDelayDays,
  investigationCheckStates,
  isLateEntry,
  openInvestigationCheckCount,
  FinanceTransactionKind,
  FinanceTransactionStatus,
  FinanceTransactionType,
  type FinanceTransactionInvestigationFacts,
  type MoneyString,
} from "@ibada/shared-types/finance"
import { InvestigationTone, type EntityInvestigation, type InvestigationRef } from "@ibada/shared-types/investigation"

/**
 * The investigation fact sheet (option I2): pure rules behind the page, kept apart from the markup so they can be
 * tested on their own. Nothing here decides access or disclosure; it only reads what the server already sent.
 */

// The check rules (threshold, delay, which checks apply and their state) live in shared-types so the
// investigation PDF the API prints reads them from the same place as this page.
export { InvestigationCheckId, InvestigationCheckStatus, LATE_ENTRY_THRESHOLD_DAYS, entryDelayDays, isLateEntry }

type Translate = (key: string, values?: Record<string, string | number>) => string

/* ------------------------------------------------------------------ */
/* Status pills                                                         */
/* ------------------------------------------------------------------ */

export type SheetPill = { label: string; tone: InvestigationTone }

/**
 * The header pills in the reader's language, from enums rather than from the English badge labels. A transaction
 * states its posting status (a reversal says so instead of "posted") and whether its entry balances.
 */
export function transactionPills(
  facts: FinanceTransactionInvestigationFacts,
  balanced: boolean | null,
  t: Translate,
): SheetPill[] {
  const pills: SheetPill[] = []
  if (facts.kind === FinanceTransactionKind.Reversal) {
    pills.push({ label: t("sheet.status.reversal"), tone: InvestigationTone.Destructive })
  }
  if (facts.status === FinanceTransactionStatus.Cancelled) {
    pills.push({ label: t("sheet.status.reversed"), tone: InvestigationTone.Destructive })
  } else if (facts.status === FinanceTransactionStatus.Voided) {
    pills.push({ label: t("sheet.status.voided"), tone: InvestigationTone.Destructive })
  } else if (facts.kind !== FinanceTransactionKind.Reversal) {
    pills.push({ label: t("sheet.status.posted"), tone: InvestigationTone.Success })
  }
  if (balanced !== null) pills.push(balancePill(balanced, t))
  return pills
}

export function balancePill(balanced: boolean, t: Translate): SheetPill {
  return balanced
    ? { label: t("sheet.status.balanced"), tone: InvestigationTone.Success }
    : { label: t("sheet.status.unbalanced"), tone: InvestigationTone.Destructive }
}

/** Money coming in reads teal, money going out rose; anything else stays neutral. */
export enum SheetAmountDirection {
  In = "in",
  Out = "out",
  Neutral = "neutral",
}

export function amountDirection(type: string | null | undefined): SheetAmountDirection {
  if (type === FinanceTransactionType.Income || type === FinanceTransactionType.TransferIn) return SheetAmountDirection.In
  if (type === FinanceTransactionType.Expense || type === FinanceTransactionType.TransferOut) return SheetAmountDirection.Out
  return SheetAmountDirection.Neutral
}

/* ------------------------------------------------------------------ */
/* Checks ("Vérifications")                                             */
/* ------------------------------------------------------------------ */

export type InvestigationCheckAction = { label: string; href: string }

export type InvestigationCheck = {
  id: InvestigationCheckId
  status: InvestigationCheckStatus
  title: string
  detail: string
  /** Only when a real feature can resolve it; otherwise the row is information only. */
  action: InvestigationCheckAction | null
}

export type InvestigationCheckLinks = {
  /** Where a donor receipt can be issued (the transaction in the transactions list), when the reader may. */
  receipt: string | null
  /** Where the account's reconciliation can be started, when the reader may. */
  reconcile: string | null
}

type CheckFormatters = {
  formatDate: (iso: string) => string
  formatMoney: (amount: MoneyString) => string
}

/**
 * Every check the investigation can state about a transaction, in order. Checks that do not apply (a donor
 * receipt on an expense, a reconciliation without a treasury account) are left out rather than shown as passed.
 */
export function buildInvestigationChecks(
  investigation: Pick<EntityInvestigation, "accountingImpact">,
  facts: FinanceTransactionInvestigationFacts | null | undefined,
  links: InvestigationCheckLinks,
  t: Translate,
  format: CheckFormatters,
): InvestigationCheck[] {
  const checks: InvestigationCheck[] = []
  const impact = investigation.accountingImpact
  // Which checks apply and their state come from the shared rules, the same ones the investigation PDF prints.
  const states = new Map(investigationCheckStates(impact, facts).map((state) => [state.id, state.status]))
  const statusOf = (id: InvestigationCheckId) => states.get(id) ?? InvestigationCheckStatus.Ok

  if (impact) {
    checks.push({
      id: InvestigationCheckId.Balanced,
      status: statusOf(InvestigationCheckId.Balanced),
      title: t("sheet.checks.balanced.title"),
      detail: impact.balanced
        ? t("sheet.checks.balanced.ok")
        : t("sheet.checks.balanced.ko", {
            debit: format.formatMoney(impact.totalDebit),
            credit: format.formatMoney(impact.totalCredit),
          }),
      action: null,
    })
  }

  if (!facts) return checks

  const days = entryDelayDays(facts.occurredAt, facts.recordedAt)
  const late = isLateEntry(days)
  checks.push({
    id: InvestigationCheckId.LateEntry,
    status: statusOf(InvestigationCheckId.LateEntry),
    title: t(late ? "sheet.checks.late.title" : "sheet.checks.late.onTimeTitle"),
    detail: late
      ? t("sheet.checks.late.late", {
          occurred: format.formatDate(facts.occurredAt),
          recorded: format.formatDate(facts.recordedAt),
          days,
        })
      : days === 0
        ? t("sheet.checks.late.sameDay")
        : t("sheet.checks.late.within", { days: LATE_ENTRY_THRESHOLD_DAYS }),
    action: null,
  })

  if (facts.evidence.applicable) {
    checks.push({
      id: InvestigationCheckId.Evidence,
      status: statusOf(InvestigationCheckId.Evidence),
      title: t("sheet.checks.evidence.title"),
      detail: facts.evidence.present
        ? facts.paymentReference
          ? t("sheet.checks.evidence.reference", { reference: facts.paymentReference })
          : t("sheet.checks.evidence.receipt")
        : t("sheet.checks.evidence.missing"),
      // Attaching a document is a documented deferral: there is nothing to link to, so the row only states it.
      action: null,
    })
  }

  if (facts.receipt.applicable) {
    const { state, receiptNumber, issuedAt } = facts.receipt
    const member = facts.member?.name ?? null
    checks.push({
      id: InvestigationCheckId.Receipt,
      status: statusOf(InvestigationCheckId.Receipt),
      title: t("sheet.checks.receipt.title"),
      detail:
        state === FinanceInvestigationReceiptState.Issued
          ? issuedAt
            ? t("sheet.checks.receipt.issuedOn", { number: receiptNumber ?? "", date: format.formatDate(issuedAt) })
            : t("sheet.checks.receipt.issued", { number: receiptNumber ?? "" })
          : state === FinanceInvestigationReceiptState.Cancelled
            ? t("sheet.checks.receipt.cancelled", { number: receiptNumber ?? "" })
            : member
              ? t("sheet.checks.receipt.noneFor", { member })
              : t("sheet.checks.receipt.none"),
      action:
        state !== FinanceInvestigationReceiptState.Issued && links.receipt
          ? { label: t("sheet.checks.receipt.action"), href: links.receipt }
          : null,
    })
  }

  if (facts.reconciliation) {
    const { state, reconciledThrough } = facts.reconciliation
    const account = facts.financeAccount?.name ?? t("sheet.checks.reconciliation.theAccount")
    const through = reconciledThrough ? format.formatDate(reconciledThrough) : ""
    checks.push({
      id: InvestigationCheckId.Reconciliation,
      status: statusOf(InvestigationCheckId.Reconciliation),
      title: t("sheet.checks.reconciliation.title"),
      detail:
        state === FinanceInvestigationReconciliationState.Covered
          ? t("sheet.checks.reconciliation.covered", { account, date: through })
          : state === FinanceInvestigationReconciliationState.NotCovered
            ? t("sheet.checks.reconciliation.behind", { account, date: through })
            : t("sheet.checks.reconciliation.never", { account }),
      action:
        state !== FinanceInvestigationReconciliationState.Covered && links.reconcile
          ? { label: t("sheet.checks.reconciliation.action"), href: links.reconcile }
          : null,
    })
  }

  return checks
}

/** The open points: everything not marked done. */
export function openCheckCount(checks: readonly InvestigationCheck[]): number {
  return openInvestigationCheckCount(checks)
}

/* ------------------------------------------------------------------ */
/* Typed links ("Liée à")                                               */
/* ------------------------------------------------------------------ */

export enum InvestigationLinkType {
  Donor = "donor",
  Member = "member",
  Fund = "fund",
  Budget = "budget",
  Account = "account",
  AccountingLine = "accountingLine",
  Period = "period",
  Program = "program",
  Activity = "activity",
  Reverses = "reverses",
  ReversedBy = "reversedBy",
  Entry = "entry",
}

/** A ref entity the treasury-account row uses; not part of the server's ref vocabulary. */
export const FINANCE_ACCOUNT_REF_ENTITY = "financeAccount"

export type InvestigationLinkRow = {
  type: InvestigationLinkType
  value: string
  ref: InvestigationRef | null
}

const ENTITY_ORDER: Record<InvestigationLinkType, number> = {
  [InvestigationLinkType.Donor]: 0,
  [InvestigationLinkType.Member]: 0,
  [InvestigationLinkType.Fund]: 1,
  [InvestigationLinkType.Budget]: 2,
  [InvestigationLinkType.Account]: 3,
  [InvestigationLinkType.AccountingLine]: 4,
  [InvestigationLinkType.Period]: 5,
  [InvestigationLinkType.Program]: 6,
  [InvestigationLinkType.Activity]: 7,
  [InvestigationLinkType.Reverses]: 8,
  [InvestigationLinkType.ReversedBy]: 9,
  [InvestigationLinkType.Entry]: 10,
}

/**
 * What the investigated object is linked to, one typed row per object, each object once.
 *
 * The server's `related` list and its business-context strip both name the fund and the account, and the journal
 * pill points back at the transaction being read: rendered side by side that put "Prémices" on the page twice
 * with nothing saying which was the fund and which the account. Rows are keyed by entity and id, the self
 * reference and the non-navigable reconciliation pills are dropped (reconciliation is a check, not a link).
 */
export function buildInvestigationLinks(
  investigation: Pick<EntityInvestigation, "header" | "related" | "accountingImpact">,
  facts: FinanceTransactionInvestigationFacts | null | undefined,
  /** Treasury accounts named without a ref (a ledger entry's context strip). */
  unlinkedAccounts: readonly string[] = [],
): InvestigationLinkRow[] {
  const rows = new Map<string, InvestigationLinkRow>()
  const add = (key: string, row: InvestigationLinkRow) => {
    if (!rows.has(key)) rows.set(key, row)
  }
  const isIncome = facts?.type === FinanceTransactionType.Income

  if (facts?.member) {
    add(`member:${facts.member.id}`, {
      type: isIncome ? InvestigationLinkType.Donor : InvestigationLinkType.Member,
      value: facts.member.name,
      ref: { entity: "member", id: facts.member.id, label: facts.member.name, clickable: true },
    })
  }
  if (facts?.financeAccount) {
    add(`${FINANCE_ACCOUNT_REF_ENTITY}:${facts.financeAccount.id}`, {
      type: InvestigationLinkType.Account,
      value: facts.financeAccount.name,
      ref: {
        entity: FINANCE_ACCOUNT_REF_ENTITY,
        id: facts.financeAccount.id,
        label: facts.financeAccount.name,
        clickable: true,
      },
    })
  }

  const accountCode = (id: string) =>
    (facts?.accountingAccount?.id === id ? facts.accountingAccount.code : null) ??
    investigation.accountingImpact?.lines.find((line) => line.accountingAccountId === id)?.code ??
    null

  for (const ref of investigation.related) {
    const key = `${ref.entity === "account" ? "accountingAccount" : ref.entity}:${ref.id}`
    switch (ref.entity) {
      case "member":
        add(key, { type: isIncome ? InvestigationLinkType.Donor : InvestigationLinkType.Member, value: ref.label, ref })
        break
      case "fund":
        add(key, { type: InvestigationLinkType.Fund, value: ref.label, ref })
        break
      case "budgetLine":
        add(key, { type: InvestigationLinkType.Budget, value: ref.label, ref })
        break
      case "account":
      case "accountingAccount": {
        const code = accountCode(ref.id)
        add(key, { type: InvestigationLinkType.AccountingLine, value: code ? `${code} ${ref.label}` : ref.label, ref })
        break
      }
      case "fiscalPeriod":
        add(key, { type: InvestigationLinkType.Period, value: ref.label, ref })
        break
      case "program":
        add(key, { type: InvestigationLinkType.Program, value: ref.label, ref })
        break
      case "activity":
        add(key, { type: InvestigationLinkType.Activity, value: ref.label, ref })
        break
      case "transaction":
        if (ref.id === investigation.header.entityId) break
        add(key, {
          type: facts?.kind === FinanceTransactionKind.Reversal ? InvestigationLinkType.Reverses : InvestigationLinkType.ReversedBy,
          // The server prefixes the relationship into the pill label; the row's type column says it now.
          value: ref.label.replace(/^(Reverses|Reversed by) /, ""),
          ref,
        })
        break
      case "journal":
        // A transaction's own entry is the page being read. On a ledger entry, the other entry of a correction.
        if (facts || ref.id === investigation.header.entityId) break
        add(key, { type: InvestigationLinkType.Entry, value: ref.label, ref })
        break
      default:
        break
    }
  }

  // A ledger entry names its treasury accounts in the context strip only, with no ref: state them, unlinked.
  for (const name of unlinkedAccounts) {
    add(`unlinkedAccount:${name}`, { type: InvestigationLinkType.Account, value: name, ref: null })
  }

  return [...rows.values()].sort((left, right) => ENTITY_ORDER[left.type] - ENTITY_ORDER[right.type])
}
